import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  TrendingUp, ChevronLeft, Download, Calendar, CreditCard, Users, Building2, Mail,
  ArrowUpRight, ArrowDownRight, RefreshCw
} from "lucide-react";
import axios from "axios";

const API = '/api';

// Helper function to calculate percentage change
const calculateChange = (current, previous) => {
  if (!previous || previous === 0) return null;
  const change = ((current - previous) / previous) * 100;
  return change.toFixed(1);
};

// Report Card Component
const ReportCard = ({ title, value, previousValue, icon: Icon, color, onClick, subtext }) => {
  const change = calculateChange(
    typeof value === 'string' ? parseFloat(value.replace(/[£,]/g, '')) : value,
    previousValue ? (typeof previousValue === 'string' ? parseFloat(previousValue.replace(/[£,]/g, '')) : previousValue) : null
  );

  return (
    <div 
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-blue-300 transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-lg ${color?.replace('text-', 'bg-').replace('-600', '-100') || 'bg-gray-100'}`}>
          <Icon className={`w-5 h-5 ${color || 'text-gray-600'}`} />
        </div>
        {change !== null && (
          <div className={`flex items-center text-xs ${parseFloat(change) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {parseFloat(change) >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(parseFloat(change))}%
          </div>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-1">{title}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
    </div>
  );
};

/**
 * Kyle Rise Reports Page
 * 
 * Revenue reports with drill-down to underlying transactions
 */
export default function KyleRiseReports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("month");
  const [summary, setSummary] = useState(null);
  const [comparePrev, setComparePrev] = useState(null);

  useEffect(() => { fetchReports(); }, [dateRange]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const now = new Date();
      let dateFrom = null;
      let prevFrom = null;
      let prevTo = null;

      if (dateRange === "today") {
        dateFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
        prevFrom = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate()).toISOString();
        prevTo = dateFrom;
      } else if (dateRange === "week") {
        const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate() - 7);
        dateFrom = weekAgo.toISOString();
        const twoWeeksAgo = new Date(now); twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        prevFrom = twoWeeksAgo.toISOString();
        prevTo = dateFrom;
      } else if (dateRange === "month") {
        const monthAgo = new Date(now); monthAgo.setMonth(monthAgo.getMonth() - 1);
        dateFrom = monthAgo.toISOString();
        const twoMonthsAgo = new Date(now); twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
        prevFrom = twoMonthsAgo.toISOString();
        prevTo = dateFrom;
      } else if (dateRange === "year") {
        const yearAgo = new Date(now); yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        dateFrom = yearAgo.toISOString();
      }

      const params = dateFrom ? `?date_from=${dateFrom}` : "";
      const res = await axios.get(`${API}/kyle-rise/summary${params}`);
      setSummary(res.data);

      if (prevFrom && prevTo) {
        const prevRes = await axios.get(`${API}/kyle-rise/summary?date_from=${prevFrom}&date_to=${prevTo}`);
        setComparePrev(prevRes.data);
      } else {
        setComparePrev(null);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="kyle-rise-reports">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/kyle-rise")} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Reports</h1>
              <p className="text-sm text-gray-500">Revenue and activity reports</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
            </select>
            <button onClick={fetchReports} className="p-2 hover:bg-gray-100 rounded-lg">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Revenue Summary */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ReportCard
              title="Total Revenue"
              value={summary?.revenue?.total_display || "£0.00"}
              previousValue={comparePrev?.revenue?.total_display}
              icon={TrendingUp}
              color="text-green-600"
              onClick={() => navigate("/admin/kyle-rise/payments?status=completed")}
            />
            <ReportCard
              title="Hourly Payments"
              value={summary?.revenue?.breakdown?.payments?.display || "£0.00"}
              previousValue={comparePrev?.revenue?.breakdown?.payments?.display}
              icon={CreditCard}
              color="text-blue-600"
              onClick={() => navigate("/admin/kyle-rise/payments?status=completed")}
              subtext={`${summary?.payments?.completed || 0} transactions`}
            />
            <ReportCard
              title="Resident Passes"
              value={summary?.revenue?.breakdown?.resident_passes?.display || "£0.00"}
              previousValue={comparePrev?.revenue?.breakdown?.resident_passes?.display}
              icon={Users}
              color="text-purple-600"
              onClick={() => navigate("/admin/kyle-rise/resident-passes?status=active")}
              subtext={`${summary?.resident_passes?.active || 0} active`}
            />
            <ReportCard
              title="Staff Passes"
              value={summary?.revenue?.breakdown?.staff_passes?.display || "£0.00"}
              previousValue={comparePrev?.revenue?.breakdown?.staff_passes?.display}
              icon={Building2}
              color="text-orange-600"
              onClick={() => navigate("/admin/kyle-rise/staff-passes?status=active")}
              subtext={`${summary?.staff_passes?.active || 0} active`}
            />
          </div>
        </div>

        {/* Activity Summary */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ReportCard
              title="Total Payments"
              value={summary?.payments?.total || 0}
              previousValue={comparePrev?.payments?.total}
              icon={CreditCard}
              onClick={() => navigate("/admin/kyle-rise/payments")}
              subtext={`${summary?.payments?.pending || 0} pending`}
            />
            <ReportCard
              title="Total Passes"
              value={(summary?.resident_passes?.total || 0) + (summary?.staff_passes?.total || 0)}
              previousValue={comparePrev ? (comparePrev.resident_passes?.total || 0) + (comparePrev.staff_passes?.total || 0) : null}
              icon={Users}
              onClick={() => navigate("/admin/kyle-rise/resident-passes")}
              subtext={`${summary?.resident_passes?.total || 0} resident, ${summary?.staff_passes?.total || 0} staff`}
            />
            <ReportCard
              title="New Enquiries"
              value={summary?.interests?.new || 0}
              previousValue={comparePrev?.interests?.new}
              icon={Building2}
              color="text-indigo-600"
              onClick={() => navigate("/admin/kyle-rise/interests?status=new")}
              subtext={`${summary?.interests?.converted || 0} converted`}
            />
            <ReportCard
              title="Emails Sent"
              value={summary?.email_logs?.sent || 0}
              previousValue={comparePrev?.email_logs?.sent}
              icon={Mail}
              onClick={() => navigate("/admin/kyle-rise/email-logs?status=sent")}
              subtext={`${summary?.email_logs?.total || 0} total`}
            />
          </div>
        </div>

        {/* Quick Export */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Export Reports</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button
              onClick={() => navigate("/admin/kyle-rise/payments")}
              className="flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm">Payments</span>
            </button>
            <button
              onClick={() => navigate("/admin/kyle-rise/resident-passes")}
              className="flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm">Resident Passes</span>
            </button>
            <button
              onClick={() => navigate("/admin/kyle-rise/staff-passes")}
              className="flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm">Staff Passes</span>
            </button>
            <button
              onClick={() => navigate("/admin/kyle-rise/retailers")}
              className="flex items-center justify-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm">Retailers</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
